import React, { useState, useEffect, useRef, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Box,
  Typography,
  Grid,
  Button,
  CircularProgress,
  Alert,
  Divider,
  InputAdornment,
  MenuItem,
  Autocomplete,
  FormControl,
  FormControlLabel,
  InputLabel,
  Select,
  Paper,
  IconButton,
  Checkbox,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Radio,
  RadioGroup,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import FavoriteIcon from "@mui/icons-material/Favorite";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ErrorIcon from "@mui/icons-material/Error";
import LockIcon from "@mui/icons-material/Lock";
import VolunteerActivismIcon from "@mui/icons-material/VolunteerActivism";
import PersonIcon from "@mui/icons-material/Person";
import LogoutIcon from "@mui/icons-material/Logout";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import axios from "axios";
import { LoaderComponent } from "../../components/Icon/Index";
import { getItem, setItem, removeItem } from "../../utils/storage";
import { jwtDecode } from "jwt-decode";
import { cleanupPaymentMethods, loadCardScript } from "../../plugins/helpers";
import { nmiCollectPosInlineStyleConfig } from "../../plugins/collectJsNmiPosShared";
import Address, { getCountryNameFromCodeOrName } from "../../components/Address";
import { useDejavooPayment } from "../../contexts/DejavooPaymentContext";
import { formatCardNumberDisplay, formatExpiryDisplay } from "../../utils/gatewayHelper";
import InputText from "../../components/InputFields/InputText";
import DollerInput from "../../components/InputFields/Doller";

// Default logo for Cloud Donor Manager
const DEFAULT_LOGO = "/DONOR_LOGO.svg";

const presetAmounts = [25, 50, 100, 250, 500, 1000];

// Default theme when companyData not yet loaded
const DEFAULT_THEME = {
  primary: "#29B3E2",
  headerBg: "#E9F8FC",
  accent: "#66D59A",
  iconColor: "#29B3E2",
  textColor: "#0f2b46",
};

// Styled components for better design
const StyledCard = styled(Paper)(({ theme }) => ({
  backgroundColor: "#ffffff",
  borderRadius: "12px",
  boxShadow: "var(--shadow-md)",
  padding: "24px",
  marginBottom: "20px",
  border: "1px solid #e8eef3",
}));

const SectionTitle = styled(Typography)(({ textColor }) => ({
  color: textColor || "#0f2b46",
  fontWeight: 600,
  fontSize: "16px",
  marginBottom: "16px",
  display: "flex",
  alignItems: "center",
  gap: "8px",
}));

const PublicDonation = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [companyData, setCompanyData] = useState(null);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [transactionData, setTransactionData] = useState(null);
  const [selectedPresetAmount, setSelectedPresetAmount] = useState(null);
  
  // Card payment state
  const [scriptGenerating, setScriptGenerating] = useState(false);
  const [scriptError, setScriptError] = useState("");
  const [isPublicKeyAvailable, setIsPublicKeyAvailable] = useState(false);
  const [cardToken, setCardToken] = useState(null);
  const [cardFieldsValid, setCardFieldsValid] = useState({
    ccnumber: false,
    ccexp: false,
    cvv: false,
  });
  const [cardFieldErrors, setCardFieldErrors] = useState({
    ccnumber: "",
    ccexp: "",
    cvv: "",
    cardError: "",
  });
  const [cardFieldsTouched, setCardFieldsTouched] = useState({
    ccnumber: false,
    ccexp: false,
    cvv: false,
  });
  
  // Track if form submission was attempted (to show card errors only after clicking submit)
  const [formSubmitAttempted, setFormSubmitAttempted] = useState(false);
  
  
  // Card fee configuration for transaction fee
  const [cardFeeConfig, setCardFeeConfig] = useState(null);
  
  // Donor login state (for existing donors to pre-fill and link donation)
  const [loginModalOpen, setLoginModalOpen] = useState(false);
  const [loginStep, setLoginStep] = useState("email"); // 'email' | 'select' | 'password'
  const [loginEmail, setLoginEmail] = useState("");
  const [loginPassword, setLoginPassword] = useState("");
  const [loginDonors, setLoginDonors] = useState([]); // donors from check-email
  const [selectedDonorId, setSelectedDonorId] = useState("");
  const [loginLoading, setLoginLoading] = useState(false);
  const [loginError, setLoginError] = useState("");
  const [showLoginPassword, setShowLoginPassword] = useState(false);
  const [donorProfile, setDonorProfile] = useState(null); // { firstName, lastName, email, phone, ... }
  
  const cardDataRef = useRef({ cardType: "", ccNumber: "" });
  const formikValuesRef = useRef(null);

  // Gateway: NMI (PublicKey) or DEJAVOO
  const gatewayType = (companyData?.gatewayType || "").toString().toUpperCase();
  const isDejavoo = gatewayType === "DEJAVOO";

  // Dejavoo payment (same as Accept Donation page)
  const { dejavoo } = useDejavooPayment();
  const [dejavooCardData, setDejavooCardData] = useState({
    ccnumber: "",
    ccexpiry: "",
    cccvv: "",
  });
  const [dejavooCardValid, setDejavooCardValid] = useState({
    ccnumber: false,
    ccexp: false,
    cvv: false,
  });
  const [dejavooCardErrors, setDejavooCardErrors] = useState({
    ccnumber: "",
    ccexp: "",
    cvv: "",
  });

  // Theme from company settings (logo + theme colors)
  const theme = companyData?.themeColors
    ? {
        primary: companyData.themeColors.primary || DEFAULT_THEME.primary,
        headerBg: companyData.themeColors.headerBg || DEFAULT_THEME.headerBg,
        accent: companyData.themeColors.accent || DEFAULT_THEME.accent,
        iconColor: companyData.themeColors.iconColor ?? DEFAULT_THEME.iconColor,
        textColor: companyData.themeColors.textColor ?? DEFAULT_THEME.textColor,
      }
    : DEFAULT_THEME;

  // Common MenuProps configuration for Select dropdowns to prevent clipping issues
  const selectMenuProps = {
    disablePortal: false,
    container: typeof document !== 'undefined' ? document.body : null,
    getContentAnchorEl: null,
    disableScrollLock: true,
    variant: "menu",
    PaperProps: {
      sx: {
        borderRadius: "8px",
        mt: 0.5,
        boxShadow: "var(--shadow-lg)",
        maxHeight: 300,
        zIndex: 1500,
      },
    },
    anchorOrigin: {
      vertical: "bottom",
      horizontal: "left",
    },
    transformOrigin: {
      vertical: "top",
      horizontal: "left",
    },
  };

  // Calculate totals with transaction fee
  const calculateTotals = (amount) => {
    const donationAmount = parseFloat(amount) || 0;
    let convenienceFee = 0;
    
    if (cardFeeConfig?.FeeAmount > 0) {
      if (cardFeeConfig.FeeType === "Percentage") {
        convenienceFee = (donationAmount * cardFeeConfig.FeeAmount) / 100;
      } else if (cardFeeConfig.FeeType === "Fixed") {
        convenienceFee = cardFeeConfig.FeeAmount;
      }
    }
    
    // Round to 2 decimal places
    convenienceFee = Math.round(convenienceFee * 100) / 100;
    const total = Math.round((donationAmount + convenienceFee) * 100) / 100;
    
    return {
      donationAmount,
      convenienceFee,
      total,
      showFee: cardFeeConfig?.FeeAmount > 0,
      feeLabel: cardFeeConfig?.FeeLabel || "Convenience Fee",
    };
  };

  // Format phone number
  const formatPhoneNumber = (value) => {
    const phoneNumber = value.replace(/[^\d]/g, "");
    const limitedPhoneNumber = phoneNumber.slice(0, 10);
    const match = limitedPhoneNumber.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      let formattedNumber = "";
      if (match[1].length >= 3) {
        formattedNumber = `(${match[1]}) `;
      } else {
        formattedNumber = match[1];
      }
      formattedNumber += match[2];
      if (match[3]) {
        formattedNumber += `-${match[3]}`;
      }
      return formattedNumber;
    }
    return value;
  };

  // Validation schema
  const validationSchema = Yup.object({
    firstName: Yup.string().required("First name is required"),
    lastName: Yup.string().required("Last name is required"),
    email: Yup.string()
      .email("Invalid email")
      .required("Email is required")
      .matches(/^[^@]+@[^@]+\.[^@]+$/, "Invalid email"),
    phone: Yup.string()
      .required("Phone number is required")
      .matches(
        /^\(\d{3}\) \d{3}-\d{4}$/,
        "Phone number must be in the format (xxx) xxx-xxxx"
      ),
    amount: Yup.number()
      .min(1, "Minimum donation is $1")
      .required("Donation amount is required"),
    address: Yup.string(),
    city: Yup.string(),
    state: Yup.string(),
    zip: Yup.string(),
    Address: Yup.string(),
    City: Yup.string(),
    State: Yup.string(),
    Zip: Yup.string(),
    Country: Yup.string(),
  });

  const formik = useFormik({
    initialValues: {
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      amount: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      country: "United States",
      Address: "",
      City: "",
      State: "",
      Zip: "",
      Country: "United States",
      notes: "",
      isAnonymous: false,
      isRecurring: false,
      frequency: "Monthly",
      frequency_interval: "",
      weekday: "",
      day_of_month: "1",
      month: "",
      day_of_year: "",
      days_after_quarter: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      // Payment is processed directly in the CollectJS callback
      // This onSubmit is kept for form validation purposes
    },
  });

  // Keep formik values ref updated
  useEffect(() => {
    formikValuesRef.current = formik.values;
  }, [formik.values]);

  // Sync Address component fields (Address, City, ...) to lowercase (address, city, ...) for ref/API
  useEffect(() => {
    const v = formik.values;
    if (v.Address !== undefined && v.address !== v.Address) formik.setFieldValue("address", v.Address, false);
    if (v.City !== undefined && v.city !== v.City) formik.setFieldValue("city", v.City, false);
    if (v.State !== undefined && v.state !== v.State) formik.setFieldValue("state", v.State, false);
    if (v.Zip !== undefined && v.zip !== v.Zip) formik.setFieldValue("zip", v.Zip, false);
    if (v.Country !== undefined && v.country !== v.Country) formik.setFieldValue("country", v.Country, false);
  }, [formik.values.Address, formik.values.City, formik.values.State, formik.values.Zip, formik.values.Country]);

  const handleAddressChange = (e) => {
    formik.handleChange(e);
    const name = e?.target?.name;
    const value = e?.target?.value;
    if (name === "Address") formik.setFieldValue("address", value, false);
    if (name === "City") formik.setFieldValue("city", value, false);
    if (name === "State") formik.setFieldValue("state", value, false);
    if (name === "Zip") formik.setFieldValue("zip", value, false);
    if (name === "Country") formik.setFieldValue("country", value, false);
  };

  // Fetch company data using token
  useEffect(() => {
    const fetchCompanyData = async () => {
      if (!token) {
        setError("Invalid or missing donation link");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.get(
          `${import.meta.env.VITE_BASE_API}/v1/public-donation/validate-token?token=${token}`
        );

        if (response.data.success) {
          setCompanyData(response.data.data);
          // Set card fee config for transaction fee calculation
          if (response.data.data.cardFeeConfig) {
            setCardFeeConfig(response.data.data.cardFeeConfig);
          }
        } else {
          setError(response.data.message || "Organization not found");
        }
      } catch (err) {
        console.error("Error fetching company:", err);
        setError(
          err.response?.data?.message || 
          "Invalid or expired donation link"
        );
      } finally {
        setLoading(false);
      }
    };

    fetchCompanyData();
  }, [token]);

  // Restore donor session if valid (same company, token not expired)
  useEffect(() => {
    if (!companyData?.companyId) return;
    const donorToken = getItem("donorToken");
    if (!donorToken) return;
    try {
      const decoded = jwtDecode(donorToken);
      if (decoded?.CompanyId === companyData.companyId && decoded?.Role === "Donor") {
        const storedProfile = sessionStorage.getItem(`donationPageDonorProfile_${companyData.companyId}`);
        if (storedProfile) {
          const profile = JSON.parse(storedProfile);
          setDonorProfile(profile);
          const countryName = getCountryNameFromCodeOrName(profile.country);
          formik.setValues((prev) => ({
            ...prev,
            firstName: profile.firstName || "",
            lastName: profile.lastName || "",
            email: profile.email || "",
            phone: profile.phone ? formatPhoneNumber(profile.phone) : "",
            address: profile.address || "",
            city: profile.city || "",
            state: profile.state || "",
            zip: profile.zip || "",
            country: countryName,
            Address: profile.address || "",
            City: profile.city || "",
            State: profile.state || "",
            Zip: profile.zip || "",
            Country: countryName,
          }));
        }
      } else {
        removeItem("donorToken");
        removeItem("DonorId");
      }
    } catch {
      removeItem("donorToken");
      removeItem("DonorId");
    }
  }, [companyData?.companyId]);

  // Restore donor session if valid (same company, token not expired)
  
  useEffect(() => {
    if (!companyData?.companyId) return;
    const donorToken = getItem("donorToken");
    if (!donorToken) return;
    try {
      const decoded = jwtDecode(donorToken);
      if (decoded?.CompanyId === companyData.companyId && decoded?.Role === "Donor") {
        const storedProfile = sessionStorage.getItem(`donationPageDonorProfile_${companyData.companyId}`);
        if (storedProfile) {
          const profile = JSON.parse(storedProfile);
          setDonorProfile(profile);
          const countryName = getCountryNameFromCodeOrName(profile.country);
          formik.setValues((prev) => ({
            ...prev,
            firstName: profile.firstName || "",
            lastName: profile.lastName || "",
            email: profile.email || "",
            phone: profile.phone ? formatPhoneNumber(profile.phone) : "",
            address: profile.address || "",
            city: profile.city || "",
            state: profile.state || "",
            zip: profile.zip || "",
            country: countryName,
            Address: profile.address || "",
            City: profile.city || "",
            State: profile.state || "",
            Zip: profile.zip || "",
            Country: countryName,
          }));
        }
      } else {
        removeItem("donorToken");
        removeItem("DonorId");
      }
    } catch {
      removeItem("donorToken");
      removeItem("DonorId");
    }
  }, [companyData?.companyId]);

  // Load card script when company data is available (NMI) - only if credit card is enabled
  useEffect(() => {
    if (companyData?.creditCardEnabled === false) return;
    if (companyData?.PublicKey) {
      loadNmiScript();
    }
    return () => {
      cleanupPaymentMethods();
    };
  }, [companyData]);

  // Initialize Dejavoo when company has Dejavoo gateway - only if credit card is enabled
  useEffect(() => {
    if (companyData?.creditCardEnabled === false) return;
    if (companyData?.companyId && isDejavoo && dejavoo?.status !== "loaded" && dejavoo?.status !== "loading") {
      dejavoo.initialize(companyData.companyId);
    }
  }, [companyData?.companyId, companyData?.creditCardEnabled, isDejavoo]);

  const validateDejavooCardField = (name, value) => {
    let isValid = false;
    let errorMessage = "";
    if (name === "ccnumber") {
      const cardNumber = (value || "").replace(/\s/g, "");
      isValid = /^\d{13,19}$/.test(cardNumber);
      if (!value) errorMessage = "Credit card number is required";
      else if (!isValid) errorMessage = "Card number must be 13-19 digits";
    } else if (name === "ccexpiry") {
      const expiry = (value || "").replace(/\s|\//g, "");
      if (!value) errorMessage = "Expiration date is required";
      else if (!/^\d{4}$/.test(expiry)) errorMessage = "Use MMYY";
      else {
        const month = parseInt(expiry.slice(0, 2), 10);
        const year = parseInt(expiry.slice(2, 4), 10);
        if (month < 1 || month > 12) errorMessage = "Month must be 01–12";
        else {
          const now = new Date();
          const currentYear = now.getFullYear() % 100;
          const currentMonth = now.getMonth() + 1;
          if (year < currentYear || (year === currentYear && month < currentMonth)) errorMessage = "Expiry cannot be in the past";
          else isValid = true;
        }
      }
    } else if (name === "cccvv") {
      isValid = /^\d{3,4}$/.test((value || "").replace(/\D/g, ""));
      if (!value) errorMessage = "CVV is required";
      else if (!isValid) errorMessage = "CVV must be 3–4 digits";
    }
    setDejavooCardValid((prev) => ({
      ...prev,
      [name === "ccexpiry" ? "ccexp" : name === "cccvv" ? "cvv" : name]: isValid,
    }));
    setDejavooCardErrors((prev) => ({
      ...prev,
      [name === "ccexpiry" ? "ccexp" : name === "cccvv" ? "cvv" : name]: errorMessage,
    }));
  };

  const handleDejavooCardChange = (e) => {
    const { name, value } = e.target;
    let processed = value;
    if (name === "ccexpiry") processed = value.replace(/\D/g, "").slice(0, 4);
    else if (name === "cccvv") processed = value.replace(/\D/g, "").slice(0, 4);
    else if (name === "ccnumber") processed = (value || "").replace(/\D/g, "").slice(0, 19);
    setDejavooCardData((prev) => ({ ...prev, [name]: processed }));
    validateDejavooCardField(name, processed);
  };

  // Derived: Dejavoo fields valid from current values (so button enables even if validation state lags)
  const dejavooFieldsValid =
    /^\d{13,19}$/.test((dejavooCardData.ccnumber || "").replace(/\s/g, "")) &&
    (() => {
      const exp = (dejavooCardData.ccexpiry || "").replace(/\D/g, "");
      if (!/^\d{4}$/.test(exp)) return false;
      const month = parseInt(exp.slice(0, 2), 10);
      const year = parseInt(exp.slice(2, 4), 10);
      if (month < 1 || month > 12) return false;
      const now = new Date();
      const currentYear = now.getFullYear() % 100;
      const currentMonth = now.getMonth() + 1;
      return year > currentYear || (year === currentYear && month >= currentMonth);
    })() &&
    /^\d{3,4}$/.test((dejavooCardData.cccvv || "").replace(/\D/g, ""));

  const loadNmiScript = async () => {
    setScriptGenerating(true);
    setScriptError("");

    try {
      cleanupPaymentMethods();
      
      if (!companyData.PublicKey) {
        throw new Error("Payment gateway not configured");
      }
      
      await loadCardScript(companyData.PublicKey);
      setIsPublicKeyAvailable(true);
    } catch (err) {
      console.error("Error loading card script:", err);
      let errorMessage = "Failed to load payment form. Please refresh the page.";
      
      if (err?.message?.includes("Invalid tokenization key format")) {
        errorMessage = "Payment gateway configuration error. Please contact the organization.";
      } else if (err?.message?.includes("Tokenization key")) {
        errorMessage = "Payment gateway not properly configured.";
      } else if (err?.message) {
        errorMessage = err.message;
      }
      
      setScriptError(errorMessage);
      setIsPublicKeyAvailable(false);
    } finally {
      setScriptGenerating(false);
    }
  };

  // Configure CollectJS
  /* global CollectJS */
  useEffect(() => {
    const initializeCollectJS = () => {
      try {
        if (typeof CollectJS !== "undefined" && isPublicKeyAvailable && companyData?.PublicKey) {
          CollectJS.configure({
            tokenizationKey: companyData.PublicKey,
            validationCallback: function (field, status, message) {
              setCardFieldsValid((prev) => ({
                ...prev,
                [field]: status,
              }));
              
              let errorMessage = "";
              if (!status) {
                const messageLower = (message || "").toLowerCase();
                const isEmptyMessage = messageLower.includes("empty") || messageLower.includes("required");
                
                if (field === "ccnumber") {
                  if (isEmptyMessage) {
                    errorMessage = "Credit card number is required";
                  } else {
                    errorMessage = message || "Invalid credit card number";
                  }
                } else if (field === "ccexp") {
                  if (isEmptyMessage) {
                    errorMessage = "Expiration date is required";
                  } else if (messageLower.includes("expired") || messageLower.includes("past")) {
                    errorMessage = "Expiration date cannot be in the past";
                  } else {
                    errorMessage = message || "Invalid expiration date";
                  }
                } else if (field === "cvv") {
                  if (isEmptyMessage) {
                    errorMessage = "CVV is required";
                  } else {
                    errorMessage = message || "Invalid CVV";
                  }
                }
              }
              
              setCardFieldErrors((prev) => ({
                ...prev,
                [field]: errorMessage,
              }));
              
              setCardFieldsTouched((prev) => ({
                ...prev,
                [field]: true,
              }));
            },
            callback: async function (response) {
              if (response?.token) {
                setCardToken(response.token);
                cardDataRef.current = {
                  cardType: response.card?.type || "",
                  ccNumber: response.card?.number || "",
                };
                setCardFieldErrors((prev) => ({ ...prev, cardError: "" }));

                // Process payment directly in callback
                try {
                  const currentValues = formikValuesRef.current;
                  
                  // Calculate totals with transaction fee
                  const totals = calculateTotals(currentValues.amount);
                  
                  // Debug: Log address values being sent
                  console.log("Sending donation with address data:", {
                    address: currentValues.Address ?? currentValues.address,
                    city: currentValues.City ?? currentValues.city,
                    state: currentValues.State ?? currentValues.state,
                    zip: currentValues.Zip ?? currentValues.zip,
                    country: currentValues.Country ?? currentValues.country,
                  });
                  console.log("Sending donation with totals:", totals);
                  
                  const isRecurring = currentValues.isRecurring === true;
                  const payload = {
                    firstName: currentValues.firstName,
                    lastName: currentValues.lastName,
                    email: currentValues.email,
                    phone: currentValues.phone || "",
                    amount: isRecurring ? totals.donationAmount : totals.total,
                    originalAmount: totals.donationAmount,
                    transactionFee: totals.convenienceFee,
                    convenienceFee: totals.convenienceFee,
                    address: currentValues.Address ?? currentValues.address,
                    city: currentValues.City ?? currentValues.city,
                    state: currentValues.State ?? currentValues.state,
                    zip: currentValues.Zip ?? currentValues.zip,
                    country: currentValues.Country ?? currentValues.country,
                    notes: currentValues.notes,
                    isAnonymous: currentValues.isAnonymous,
                    paymentToken: response.token,
                    cardType: response.card?.type || "",
                    ccNumber: response.card?.number || "",
                  };
                  if (isRecurring) {
                    payload.isRecurring = true;
                    payload.IsDonation = true;
                    payload.frequency = currentValues.frequency || "Monthly";
                    payload.frequency_interval = currentValues.frequency_interval || "";
                    payload.weekday = currentValues.weekday || null;
                    payload.day_of_month = currentValues.day_of_month ? String(currentValues.day_of_month) : null;
                    payload.month = currentValues.month ? String(currentValues.month) : null;
                    payload.day_of_year = currentValues.day_of_year ? String(currentValues.day_of_year) : null;
                    payload.days_after_quarter = currentValues.days_after_quarter !== "" && currentValues.days_after_quarter != null ? String(currentValues.days_after_quarter) : null;
                  }

                  const donorToken = getItem("donorToken");
                  const apiConfig = {
                    headers: donorToken ? { Authorization: `CMS ${donorToken}` } : {},
                  };
                  const apiResponse = await axios.post(
                    `${import.meta.env.VITE_BASE_API}/v1/public-donation/donate?token=${token}`,
                    payload,
                    apiConfig
                  );

                  if (apiResponse.data.success) {
                    setSuccess(true);
                    setTransactionData(apiResponse.data.data);
                  } else {
                    setError(apiResponse.data.message || "Donation failed. Please try again.");
                  }
                } catch (err) {
                  setError(
                    err.response?.data?.message ||
                    "We couldn't process your donation. Please try again in a moment."
                  );
                } finally {
                  setSubmitting(false);
                }
              } else if (response?.error) {
                setCardFieldErrors((prev) => ({
                  ...prev,
                  cardError: response.error || "Card tokenization failed",
                }));
                setSubmitting(false);
              }
            },
            ...nmiCollectPosInlineStyleConfig,
            customCss: {
              ...nmiCollectPosInlineStyleConfig.customCss,
              border: `1px solid ${theme.primary}`,
            },
            placeholderCss: {
              ...nmiCollectPosInlineStyleConfig.placeholderCss,
              color: "#1a7390",
            },
            fields: {
              ccnumber: { placeholder: "Credit Card *" },
              ccexp: { placeholder: "MM / YY *" },
              cvv: { placeholder: "CVV *" },
            },
          });
        }
      } catch (error) {
        console.error("Error configuring CollectJS:", error);
      }
    };

    if (!scriptGenerating && isPublicKeyAvailable && companyData?.PublicKey) {
      initializeCollectJS();
    }
  }, [scriptGenerating, isPublicKeyAvailable, companyData]);

  const handlePresetAmountClick = (amount) => {
    setSelectedPresetAmount(amount);
    formik.setFieldValue("amount", amount);
  };

  // Reset login modal state when closing
  const resetLoginModal = () => {
    setLoginModalOpen(false);
    setLoginStep("email");
    setLoginError("");
    setLoginEmail("");
    setLoginPassword("");
    setShowLoginPassword(false);
    setLoginDonors([]);
    setSelectedDonorId("");
  };

  // Step 1: Check donor email (like main login)
  const handleCheckDonorEmail = async (e) => {
    e?.preventDefault?.();
    setLoginError("");
    if (!loginEmail?.trim()) {
      setLoginError("Email is required");
      return;
    }
    setLoginLoading(true);
    try {
      const res = await axios.post(
        `${import.meta.env.VITE_BASE_API}/v1/public-donation/check-donor-email?token=${token}`,
        { email: loginEmail.trim() }
      );
      if (res?.data?.success && res?.data?.data) {
        const { donors } = res.data.data;
        setLoginDonors(donors || []);
        setLoginStep("select");
        setSelectedDonorId(donors?.length === 1 ? donors[0]?.UserId || "" : "");
      } else {
        setLoginError(res?.data?.message || "No donor account found");
      }
    } catch (err) {
      setLoginError(
        err?.response?.data?.message || "No donor account found with this email for this organization"
      );
    } finally {
      setLoginLoading(false);
    }
  };

  // Step 2: Donor selection - check password status before proceeding
  const handleDonorSelectAndContinue = () => {
    if (!selectedDonorId) {
      setLoginError("Please select a donor account");
      return;
    }
    const selectedDonor = loginDonors.find((d) => d.UserId === selectedDonorId);
    if (!selectedDonor?.IsPassSet) {
      setLoginError("Password is not set for this account. Please donate as a guest or contact the organization.");
      return;
    }
    setLoginError("");
    setLoginStep("password");
  };

  // Step 3: Donor login - authenticate and pre-fill form
  const handleDonorLogin = async (e) => {
    e?.preventDefault?.();
    setLoginError("");
    if (!loginEmail?.trim() || !loginPassword) {
      setLoginError("Email and password are required");
      return;
    }
    const userIdToUse = loginDonors.length > 1 ? selectedDonorId : loginDonors?.[0]?.UserId;
    if (loginDonors.length > 1 && !userIdToUse) {
      setLoginError("Please select a donor account");
      return;
    }
    setLoginLoading(true);
    try {
      const payload = { email: loginEmail.trim(), password: loginPassword };
      if (userIdToUse) payload.userId = userIdToUse;
      const res = await axios.post(
        `${import.meta.env.VITE_BASE_API}/v1/public-donation/donor-login?token=${token}`,
        payload
      );
      if (res?.data?.success && res?.data?.data) {
        const { token: donorToken, donorId, profile } = res.data.data;
        setItem("donorToken", donorToken);
        setItem("DonorId", donorId);
        setDonorProfile(profile);
        // Store profile for session restore (keyed by company from token)
        try {
          const decoded = jwtDecode(donorToken);
          if (decoded?.CompanyId) {
            sessionStorage.setItem(
              `donationPageDonorProfile_${decoded.CompanyId}`,
              JSON.stringify(profile)
            );
          }
        } catch (e) { /* ignore */ }
        resetLoginModal();
        setLoginError("");
        // Pre-fill form
        const countryName = getCountryNameFromCodeOrName(profile.country);
        formik.setValues({
          ...formik.values,
          firstName: profile.firstName || "",
          lastName: profile.lastName || "",
          email: profile.email || "",
          phone: profile.phone ? formatPhoneNumber(profile.phone) : "",
          address: profile.address || "",
          city: profile.city || "",
          state: profile.state || "",
          zip: profile.zip || "",
          country: countryName,
          Address: profile.address || "",
          City: profile.city || "",
          State: profile.state || "",
          Zip: profile.zip || "",
          Country: countryName,
        });
      } else {
        setLoginError(res?.data?.message || "Login failed");
      }
    } catch (err) {
      setLoginError(
        err?.response?.data?.message || "Invalid email or password. Please try again."
      );
    } finally {
      setLoginLoading(false);
    }
  };

  // Donor logout - clear session and form pre-fill
  const handleDonorLogout = () => {
    const donorToken = getItem("donorToken");
    if (donorToken) {
      try {
        const decoded = jwtDecode(donorToken);
        if (decoded?.CompanyId) {
          sessionStorage.removeItem(`donationPageDonorProfile_${decoded.CompanyId}`);
        }
      } catch (e) { /* ignore */ }
    }
    removeItem("donorToken");
    removeItem("DonorId");
    setDonorProfile(null);
    formik.setValues({
      ...formik.values,
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      address: "",
      city: "",
      state: "",
      zip: "",
      country: "United States",
      Address: "",
      City: "",
      State: "",
      Zip: "",
      Country: "United States",
    });
  };

  const handleCustomAmountChange = (e) => {
    const value = e.target.value.replace(/[^0-9.]/g, "");
    setSelectedPresetAmount(null);
    formik.setFieldValue("amount", value);
  };

  // Note: Payment is now processed directly in the CollectJS callback
  // No need for useEffect to trigger form submission

  // Handle form submission with card tokenization
  const handleFormSubmit = async (e) => {
    e.preventDefault();
    
    // Mark that form submission was attempted (to show card errors)
    setFormSubmitAttempted(true);
    
    // Validate form first
    const errors = await formik.validateForm();
    formik.setTouched({
      firstName: true,
      lastName: true,
      email: true,
      phone: true,
      amount: true,
    });

    if (Object.keys(errors).length > 0) {
      return;
    }

    // Recurring: validate frequency-specific fields
    if (formik.values.isRecurring) {
      const freq = formik.values.frequency;
      if (!freq || freq === "") {
        setError("Please select a frequency for your recurring donation.");
        return;
      }
      if ((freq === "Every N Months" || freq === "Every N Weeks") && (!formik.values.frequency_interval || formik.values.frequency_interval === "")) {
        setError("Please enter an interval for your recurring donation.");
        return;
      }
      if ((freq === "Monthly" || freq === "Every N Months") && (!formik.values.day_of_month || formik.values.day_of_month === "")) {
        setError("Please enter a day of the month (1-28) for your recurring donation.");
        return;
      }
      if ((freq === "Weekly" || freq === "Every N Weeks") && !formik.values.weekday) {
        setError("Please select a day of the week for your recurring donation.");
        return;
      }
      if (freq === "Yearly" && (!formik.values.day_of_year || formik.values.day_of_year === "" || !formik.values.month || formik.values.month === "")) {
        setError("Please enter day and month for your yearly recurring donation.");
        return;
      }
      if (freq === "Quarterly" && (formik.values.days_after_quarter === "" || formik.values.days_after_quarter == null)) {
        setError("Please enter days after quarter start (0-90) for your quarterly recurring donation.");
        return;
      }
    }

    // Validate card fields (Dejavoo uses dejavooFieldsValid; NMI uses cardFieldsValid)
    if (isDejavoo) {
      if (!dejavooFieldsValid) {
        const ccRaw = (dejavooCardData.ccnumber || "").replace(/\s/g, "");
        const expRaw = (dejavooCardData.ccexpiry || "").replace(/\D/g, "");
        const cvvRaw = (dejavooCardData.cccvv || "").replace(/\D/g, "");
        setDejavooCardErrors((prev) => ({
          ...prev,
          ccnumber: !/^\d{13,19}$/.test(ccRaw) ? (ccRaw ? "Card number must be 13-19 digits" : "Credit card number is required") : "",
          ccexp: expRaw.length !== 4 ? "Expiration date is required (MMYY)" : "",
          cvv: !/^\d{3,4}$/.test(cvvRaw) ? (cvvRaw ? "CVV must be 3–4 digits" : "CVV is required") : "",
        }));
        return;
      }
    } else {
      const cardFieldsEmpty = !cardFieldsValid.ccnumber || !cardFieldsValid.ccexp || !cardFieldsValid.cvv;
      if (cardFieldsEmpty) {
        setCardFieldsTouched({
          ccnumber: true,
          ccexp: true,
          cvv: true,
        });
        const cardErrors = {};
        if (!cardFieldsValid.ccnumber) {
          cardErrors.ccnumber = "Credit card number is required";
        }
        if (!cardFieldsValid.ccexp) {
          cardErrors.ccexp = "Expiration date is required";
        }
        if (!cardFieldsValid.cvv) {
          cardErrors.cvv = "CVV is required";
        }
        setCardFieldErrors((prev) => ({ ...prev, ...cardErrors }));
        return;
      }
    }

    setSubmitting(true);
    setError(null);
    
    formikValuesRef.current = formik.values;

    // Dejavoo: get token from postData() then call donate API
    if (isDejavoo) {
      if (!dejavoo?.postData) {
        setError("Payment system not ready. Please wait for the form to load or refresh the page.");
        setSubmitting(false);
        return;
      }
      try {
        dejavoo.handleTransactionResponse(null);
        const tokenResponse = await dejavoo.postData();
        if (!tokenResponse) {
          setError("No response from payment system. Please try again.");
          setSubmitting(false);
          return;
        }
        dejavoo.handleTransactionResponse(tokenResponse);
        const cardToken =
          tokenResponse?.payment_token_id ||
          tokenResponse?.token ||
          tokenResponse?.dccd5389;
        if (tokenResponse?.statusCode !== "00" || !cardToken) {
          setError(tokenResponse?.message || "Card tokenization failed. Please try again.");
          setSubmitting(false);
          return;
        }
        // Masked card number for display/save (from token response; backend also stores from tokenize)
        const displayCcNumber =
          tokenResponse?.ccNumber || tokenResponse?.cc_number || "";
        const currentValues = formikValuesRef.current;
        const totals = calculateTotals(currentValues.amount);
        const isRecurring = currentValues.isRecurring === true;
        const payload = {
          firstName: currentValues.firstName,
          lastName: currentValues.lastName,
          email: currentValues.email,
          phone: currentValues.phone || "",
          amount: isRecurring ? totals.donationAmount : totals.total,
          originalAmount: totals.donationAmount,
          transactionFee: totals.convenienceFee,
          convenienceFee: totals.convenienceFee,
          address: currentValues.Address ?? currentValues.address,
          city: currentValues.City ?? currentValues.city,
          state: currentValues.State ?? currentValues.state,
          zip: currentValues.Zip ?? currentValues.zip,
          country: currentValues.Country ?? currentValues.country,
          notes: currentValues.notes,
          isAnonymous: currentValues.isAnonymous,
          paymentToken: cardToken,
          cardType: "",
          ccNumber: "",
          displayCcNumber: displayCcNumber || undefined,
        };
        if (isRecurring) {
          payload.isRecurring = true;
          payload.IsDonation = true;
          payload.frequency = currentValues.frequency || "Monthly";
          payload.frequency_interval = currentValues.frequency_interval || "";
          payload.weekday = currentValues.weekday || null;
          payload.day_of_month = currentValues.day_of_month ? String(currentValues.day_of_month) : null;
          payload.month = currentValues.month ? String(currentValues.month) : null;
          payload.day_of_year = currentValues.day_of_year ? String(currentValues.day_of_year) : null;
          payload.days_after_quarter = currentValues.days_after_quarter !== "" && currentValues.days_after_quarter != null ? String(currentValues.days_after_quarter) : null;
        }
        const apiResponse = await axios.post(
          `${import.meta.env.VITE_BASE_API}/v1/public-donation/donate?token=${token}`,
          payload
        );
        if (apiResponse.data.success) {
          setSuccess(true);
          setTransactionData(apiResponse.data.data);
        } else {
          setError(apiResponse.data.message || "Donation failed. Please try again.");
        }
      } catch (err) {
        setError(
          err?.response?.data?.message ||
            err?.message ||
            "We couldn't process your donation. Please try again in a moment."
        );
      } finally {
        setSubmitting(false);
      }
      return;
    }

    if (typeof CollectJS !== "undefined") {
      try {
        // Set a timeout to prevent infinite loading
        const timeoutId = setTimeout(() => {
          if (submitting) {
            setError("Payment request timed out. Please try again.");
            setSubmitting(false);
          }
        }, 30000); // 30 second timeout

        CollectJS.startPaymentRequest();
        
        // Clear timeout is handled in the callback
      } catch (err) {
        console.error("Error starting payment request:", err);
        setCardFieldErrors((prev) => ({
          ...prev,
          cardError: "Please complete all card fields",
        }));
        setSubmitting(false);
      }
    } else {
      setError("Payment system not initialized. Please refresh the page.");
      setSubmitting(false);
    }
  };

  // Loading state
  if (loading) {
    return (
      <Grid
        style={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          height: "100vh",
          backgroundColor: "#f8f9fa",
        }}
      >
        <LoaderComponent loader={loading} height="50" width="50" isDonationMode={true} />
      </Grid>
    );
  }

  // Error state (company not found)
  if (error && !companyData) {
    return (
      <Grid
        style={{
          minHeight: "100vh",
          backgroundColor: "#ffffff",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            borderRadius: "8px",
            boxShadow: "var(--shadow-md)",
            padding: "48px",
            textAlign: "center",
            maxWidth: "500px",
          }}
        >
          <ErrorIcon sx={{ fontSize: 80, color: "#dc3545", mb: 2 }} />
          <Typography variant="h5" color="error" gutterBottom>
            {error}
          </Typography>
          <Typography variant="body1" color="textSecondary">
            The donation page you're looking for is not available.
          </Typography>
        </Box>
      </Grid>
    );
  }

  // Success state
  if (success) {
    return (
      <Grid
        style={{
          minHeight: "100vh",
          backgroundColor: "#ffffff",
        }}
      >
        {/* Header */}
        <Grid
          style={{
            width: "100%",
            backgroundColor: theme.headerBg,
            padding: "24px 0",
            borderBottom: "1px solid #000000",
          }}
        >
          <Grid
            style={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              maxWidth: "1200px",
              margin: "0 auto",
              padding: "0 40px",
            }}
          >
            <img
              src={companyData?.companyLogo || DEFAULT_LOGO}
              alt={companyData?.companyName || "Cloud Donor Manager"}
              style={{
                height: "60px",
                width: "auto",
                maxWidth: "250px",
                backgroundColor: companyData?.companyLogo ? "white" : "transparent",
                padding: companyData?.companyLogo ? "8px 16px" : "0",
                borderRadius: "8px",
              }}
              onError={(e) => {
                e.target.src = DEFAULT_LOGO;
                e.target.style.backgroundColor = "transparent";
                e.target.style.padding = "0";
              }}
            />
          </Grid>
        </Grid>

        <Grid
          style={{
            maxWidth: "600px",
            margin: "60px auto",
            padding: "0 20px",
          }}
        >
          <Box
            sx={{
              backgroundColor: "white",
              borderRadius: "8px",
              boxShadow: "var(--shadow-md)",
              padding: "48px",
              textAlign: "center",
            }}
          >
            <Box
              sx={{
                width: 100,
                height: 100,
                borderRadius: "50%",
                backgroundColor: theme.primary,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                margin: "0 auto 24px",
              }}
            >
              <CheckCircleIcon sx={{ fontSize: 60, color: "white" }} />
            </Box>
            <Typography variant="h4" sx={{ color: theme.textColor, fontWeight: 700, mb: 2 }}>
              Thank You!
            </Typography>
            {transactionData?.recurringId ? (
              <>
                <Typography variant="h6" sx={{ color: "#666", mb: 2 }}>
                  Your recurring donation of <strong>${parseFloat(formik.values.amount).toFixed(2)}</strong> ({transactionData.frequency}) has been set up successfully.
                </Typography>
                {transactionData?.nextDueDate && (() => {
                  const raw = transactionData.nextDueDate;
                  const dateObj = raw ? new Date(raw + (raw.includes("T") ? "" : "T12:00:00")) : null;
                  const formatted = dateObj && !isNaN(dateObj.getTime())
                    ? dateObj.toLocaleDateString("en-US", { month: "2-digit", day: "2-digit", year: "numeric" })
                    : raw;
                  return (
                    <Typography variant="body2" sx={{ color: "#888", mb: 3 }}>
                      Your first donation will be charged on <strong>{formatted}</strong>.
                    </Typography>
                  );
                })()}
              </>
            ) : (
              <>
                <Typography variant="h6" sx={{ color: "#666", mb: 3 }}>
                  Your donation of <strong>${parseFloat(formik.values.amount).toFixed(2)}</strong> has been received.
                </Typography>
                {transactionData?.transactionId && (
                  <Typography variant="body2" sx={{ color: "#888", mb: 3 }}>
                    Transaction ID: {transactionData.transactionId}
                  </Typography>
                )}
              </>
            )}
            <Divider sx={{ my: 3 }} />
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 1 }}>
              <FavoriteIcon sx={{ color: theme.accent }} />
              <Typography variant="body1" sx={{ color: theme.textColor }}>
                Your generosity makes a difference!
              </Typography>
            </Box>
            {companyData?.companyName && (
              <Typography variant="body2" sx={{ color: "#888", mt: 2 }}>
                - {companyData.companyName}
              </Typography>
            )}
            <Button
              variant="outlined"
              sx={{ 
                mt: 4, 
                borderColor: theme.primary, 
                color: theme.primary,
                "&:hover": {
                  borderColor: theme.primary,
                  backgroundColor: `${theme.primary}1A`,
                }
              }}
              onClick={() => window.location.reload()}
            >
              Make Another Donation
            </Button>
          </Box>
        </Grid>

        {/* Footer */}
        <Box sx={{ textAlign: "center", py: 3 }}>
          <Typography variant="body2" sx={{ color: "#888" }}>
            Powered by Cloud Donor Manager
          </Typography>
        </Box>
      </Grid>
    );
  }

  // Main donation form
  return (
    <Grid
      style={{
        minHeight: "100vh",
        backgroundColor: "#ffffff",
      }}
    >
      {/* Header */}
      <Grid
        style={{
          width: "100%",
          backgroundColor: theme.headerBg,
          padding: "24px 0",
          borderBottom: "1px solid #000000",
        }}
      >
        <Grid
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            maxWidth: "1200px",
            margin: "0 auto",
            padding: "0 40px",
          }}
        >
          <img
            src={companyData?.companyLogo || DEFAULT_LOGO}
            alt={companyData?.companyName || "Cloud Donor Manager"}
            style={{
              height: "60px",
              width: "auto",
              maxWidth: "250px",
              backgroundColor: companyData?.companyLogo ? "white" : "transparent",
              padding: companyData?.companyLogo ? "8px 16px" : "0",
              borderRadius: "8px",
            }}
            onError={(e) => {
              e.target.src = DEFAULT_LOGO;
              e.target.style.backgroundColor = "transparent";
              e.target.style.padding = "0";
            }}
          />
        </Grid>
      </Grid>
      {/* Main Content */}
      <Grid
        style={{
          maxWidth: "800px",
          margin: "40px auto",
          padding: "0 20px",
        }}
      >
        {/* Title Section */}
        <Box sx={{ mb: 3 }}>
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 1, mb: 1 }}>
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <VolunteerActivismIcon sx={{ color: theme.iconColor, fontSize: 28 }} />
              <Typography
                style={{
                  color: theme.textColor,
                  fontSize: "22px",
                  fontWeight: 500,
                }}
              >
                Make a Donation
              </Typography>
            </Box>
            {donorProfile ? (
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <PersonIcon sx={{ color: theme.primary, fontSize: 20 }} />
                <Typography variant="body2" sx={{ color: theme.textColor }}>
                  Logged in as {donorProfile.firstName} {donorProfile.lastName}
                </Typography>
                <Button
                  size="small"
                  onClick={handleDonorLogout}
                  startIcon={<LogoutIcon sx={{ fontSize: 16 }} />}
                  sx={{
                    color: theme.primary,
                    textTransform: "none",
                    fontSize: "13px",
                    "&:hover": { backgroundColor: `${theme.primary}14` },
                  }}
                >
                  Log Out
                </Button>
              </Box>
            ) : (
              <Button
                size="small"
                onClick={() => setLoginModalOpen(true)}
                startIcon={<LockIcon sx={{ fontSize: 16 }} />}
                sx={{
                  color: theme.primary,
                  textTransform: "none",
                  fontSize: "13px",
                  "&:hover": { backgroundColor: `${theme.primary}14` },
                }}
              >
                Log In to save time and track your giving
              </Button>
            )}
          </Box>
          <Typography
            style={{
              color: "#64748b",
              fontSize: "14px",
            }}
          >
            to {companyData?.companyName}
          </Typography>
        </Box>

        {/* Donor Login Modal */}
        <Dialog
          open={loginModalOpen}
          onClose={() => {
            if (!loginLoading) resetLoginModal();
          }}
          PaperProps={{
            sx: {
              borderRadius: "16px",
              padding: 0,
              maxWidth: "440px",
              width: "100%",
              overflow: "hidden",
            },
          }}
        >
          <DialogTitle
            sx={{
              color: theme.textColor,
              fontWeight: 600,
              fontSize: "20px",
              pb: 1,
              borderBottom: "1px solid #eee",
            }}
          >
            Log In to your donor account
          </DialogTitle>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              if (loginStep === "email") handleCheckDonorEmail(e);
              else if (loginStep === "select") handleDonorSelectAndContinue();
              else handleDonorLogin(e);
            }}
          >
            <DialogContent sx={{ pt: 3, pb: 2, overflowX: "hidden" }}>
              {loginError && (
                <Alert severity="error" sx={{ mb: 2 }}>
                  {loginError}
                </Alert>
              )}

              {/* Step 1: Email */}
              {loginStep === "email" && (
                <InputText
                  fullWidth
                  name="email"
                  label="Email"
                  type="email"
                  value={loginEmail}
                  onChange={(e) => setLoginEmail(e.target.value)}
                  autoComplete="email"
                  inputProps={{
                    autoComplete: "email",
                  }}
                />
              )}

              {/* Step 2: Donor selection - clean single-column list */}
              {loginStep === "select" && (
                <FormControl component="fieldset" sx={{ width: "100%", display: "block" }}>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {loginDonors[0]?.EmailAddress}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ mb: 2, color: theme.textColor, fontWeight: 600 }}>
                    Select donor account
                  </Typography>
                  <Box
                    sx={{
                      maxHeight: 220,
                      overflowY: "auto",
                      overflowX: "hidden",
                      pr: 1,
                      "&::-webkit-scrollbar": { width: 8 },
                      "&::-webkit-scrollbar-track": { backgroundColor: "#f1f5f9", borderRadius: 4 },
                      "&::-webkit-scrollbar-thumb": { backgroundColor: "#94a3b8", borderRadius: 4 },
                      "&::-webkit-scrollbar-thumb:hover": { backgroundColor: "#64748b" },
                    }}
                  >
                  <RadioGroup
                    value={selectedDonorId}
                    onChange={(e) => setSelectedDonorId(e.target.value)}
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                    }}
                  >
                    {loginDonors.map((donor) => {
                      const isSelected = selectedDonorId === donor.UserId;
                      return (
                        <Box
                          key={donor.UserId}
                          onClick={() => setSelectedDonorId(donor.UserId)}
                          sx={{
                            display: "flex",
                            alignItems: "center",
                            gap: 1.5,
                            p: 2,
                            borderRadius: "12px",
                            border: `2px solid ${isSelected ? theme.primary : "#e8ecf0"}`,
                            backgroundColor: isSelected ? `${theme.primary}0D` : "#f8fafc",
                            cursor: "pointer",
                            transition: "all 0.2s ease",
                            minWidth: 0,
                            "&:hover": {
                              borderColor: isSelected ? theme.primary : "#c5d0de",
                              backgroundColor: isSelected ? `${theme.primary}0D` : "#f1f5f9",
                            },
                          }}
                        >
                          <Radio
                            size="small"
                            checked={isSelected}
                            value={donor.UserId}
                            sx={{ color: theme.primary, flexShrink: 0 }}
                          />
                          <Box sx={{ flex: 1, minWidth: 0 }}>
                            <Typography
                              variant="body1"
                              fontWeight={600}
                              sx={{ color: theme.textColor, fontSize: "15px" }}
                            >
                              {[donor.FirstName, donor.LastName].filter(Boolean).join(" ") || donor.EmailAddress}
                            </Typography>
                            <Box
                              component="span"
                              sx={{
                                display: "inline-block",
                                mt: 0.75,
                                px: 1.25,
                                py: 0.35,
                                borderRadius: "6px",
                                fontSize: "11px",
                                fontWeight: 600,
                                letterSpacing: "0.3px",
                                backgroundColor: donor.IsPassSet ? "#dcfce7" : "#fee2e2",
                                color: donor.IsPassSet ? "#166534" : "#b91c1c",
                              }}
                            >
                              {donor.IsPassSet ? "Password set" : "No password"}
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </RadioGroup>
                  </Box>
                </FormControl>
              )}

              {/* Step 3: Password */}
              {loginStep === "password" && (
                <>
                  <Typography variant="body2" sx={{ mb: 2, color: theme.textColor }}>
                    {(() => {
                      const sel = loginDonors.find((d) => d.UserId === selectedDonorId);
                      const name = sel ? [sel.FirstName, sel.LastName].filter(Boolean).join(" ") : "";
                      return `Enter password for ${name || "your account"}`;
                    })()}
                  </Typography>
                  <InputText
                    fullWidth
                    label="Password"
                    type={showLoginPassword ? "text" : "password"}
                    value={loginPassword}
                    onChange={(e) => setLoginPassword(e.target.value)}
                    autoComplete="current-password"
                    endAdornment={
                      <InputAdornment position="end">
                        <IconButton
                          onClick={() => setShowLoginPassword(!showLoginPassword)}
                          onMouseDown={(e) => e.preventDefault()}
                          edge="end"
                          aria-label={showLoginPassword ? "Hide password" : "Show password"}
                          sx={{ color: theme.textColor || "#64748b" }}
                        >
                          {showLoginPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    }
                  />
                </>
              )}
            </DialogContent>
            <DialogActions
              sx={{
                px: 3,
                py: 2,
                gap: 1.5,
                borderTop: "1px solid #eee",
                backgroundColor: "#fafbfc",
              }}
            >
              {loginStep !== "email" && (
                <Button
                  type="button"
                  onClick={() => {
                    if (loginStep === "password") {
                      setLoginStep("select");
                      setLoginPassword("");
                      setShowLoginPassword(false);
                    } else {
                      setLoginStep("email");
                      setLoginDonors([]);
                      setSelectedDonorId("");
                    }
                    setLoginError("");
                  }}
                  disabled={loginLoading}
                sx={{ color: theme.textColor, mr: "auto", textTransform: "none" }}
              >
                Back
              </Button>
              )}
              <Button
                type="button"
                onClick={resetLoginModal}
                disabled={loginLoading}
                sx={{ color: theme.textColor, textTransform: "none" }}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  loginLoading ||
                  (loginStep === "email" && !loginEmail?.trim()) ||
                  (loginStep === "select" && !selectedDonorId) ||
                  (loginStep === "password" && !loginPassword)
                }
                sx={{
                  backgroundColor: theme.primary,
                  textTransform: "none",
                  fontWeight: 600,
                  px: 2.5,
                  "&:hover": { backgroundColor: theme.primary, opacity: 0.92 },
                  "&.Mui-disabled": { backgroundColor: "#94a3b8", color: "#fff" },
                }}
              >
                {loginLoading ? (
                  <CircularProgress size={24} color="inherit" />
                ) : loginStep === "email" ? (
                  "Continue"
                ) : loginStep === "select" ? (
                  "Continue"
                ) : (
                  "Log In"
                )}
              </Button>
            </DialogActions>
          </form>
        </Dialog>

        {/* Error Alert */}
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        <form onSubmit={handleFormSubmit}>
          {/* Amount Selection */}
          <StyledCard elevation={0}>
            <SectionTitle textColor={theme.textColor}>
              <FavoriteIcon sx={{ color: theme.iconColor, fontSize: 20 }} />
              Select Donation Amount
            </SectionTitle>
            <Grid container spacing={1.5} sx={{ mb: 3 }}>
              {presetAmounts.map((amount) => (
                <Grid
                  key={amount}
                  size={{
                    xs: 4,
                    sm: 2
                  }}>
                  <Button
                    fullWidth
                    variant={selectedPresetAmount === amount ? "contained" : "outlined"}
                    onClick={() => handlePresetAmountClick(amount)}
                    type="button"
                    sx={{
                      borderRadius: "10px",
                      padding: "14px 8px",
                      fontSize: "16px",
                      borderColor: selectedPresetAmount === amount ? theme.primary : "#d1d5db",
                      borderWidth: "2px",
                      backgroundColor: selectedPresetAmount === amount ? theme.primary : "white",
                      color: selectedPresetAmount === amount ? "white" : "#374151",
                      fontWeight: 600,
                      boxShadow: selectedPresetAmount === amount 
                        ? `0 4px 12px ${theme.primary}4D` 
                        : "none",
                      transition: "all 0.2s ease",
                      "&:hover": {
                        borderColor: theme.primary,
                        borderWidth: "2px",
                        backgroundColor: selectedPresetAmount === amount ? theme.primary : `${theme.primary}14`,
                        transform: "translateY(-2px)",
                        boxShadow: `0 4px 12px ${theme.primary}33`,
                      },
                    }}
                  >
                    ${amount}
                  </Button>
                </Grid>
              ))}
            </Grid>
            <DollerInput
              name="amount"
              label="Or Enter Custom Amount"
              required
              value={formik.values.amount}
              onChange={handleCustomAmountChange}
              onBlur={formik.handleBlur}
              error={formik.touched.amount && Boolean(formik.errors.amount)}
              helperText={formik.touched.amount && formik.errors.amount}
              placeholder="0.00"
              className="w-100"
            />

            {/* Recurring donation option */}
            <StyledCard
              elevation={0}
              sx={{
                mt: 2,
                border: `1px solid ${theme.primary}33`,
                borderRadius: "12px",
                backgroundColor: `${theme.primary}08`,
                overflow: "visible",
                padding: "20px 24px 24px",
                position: "relative",
                zIndex: 1,
              }}
            >
              <SectionTitle textColor={theme.textColor} sx={{ mb: 1.5 }}>
                Recurring donation
              </SectionTitle>
              <Box sx={{ display: "flex", flexDirection: "column", alignItems: "flex-start", width: "100%" }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={formik.values.isRecurring || false}
                      onChange={(e) => formik.setFieldValue("isRecurring", e.target.checked)}
                      sx={{
                        color: theme.primary,
                        "&.Mui-checked": { color: theme.primary },
                        padding: "4px",
                        "& .MuiSvgIcon-root": { fontSize: 22 },
                      }}
                    />
                  }
                  label={
                    <Typography
                      sx={{
                        color: theme.textColor,
                        fontSize: "15px",
                        fontWeight: 600,
                        lineHeight: 1.4,
                      }}
                    >
                      Make this a recurring donation
                    </Typography>
                  }
                  sx={{
                    marginLeft: 0,
                    marginRight: 0,
                    alignItems: "center",
                    "& .MuiFormControlLabel-label": { marginLeft: 0.75 },
                  }}
                />
                <Typography
                  variant="body2"
                  sx={{
                    color: theme.textColor,
                    opacity: 0.9,
                    fontSize: "13px",
                    mt: 0.5,
                    mb: 0,
                    alignSelf: "flex-start",
                  }}
                >
                  Your card will be charged automatically on the schedule you choose below.
                </Typography>
              </Box>
              {formik.values.isRecurring && (
                <Grid container spacing={2} sx={{ mt: 2, position: "relative", zIndex: 1 }}>
                  <Grid
                    size={{
                      xs: 12,
                      sm: 6
                    }}>
                    <FormControl fullWidth size="small">
                      <InputLabel id="frequency-label" sx={{ color: theme.textColor }}>Frequency</InputLabel>
                      <Select
                        labelId="frequency-label"
                        value={formik.values.frequency || "Monthly"}
                        label="Frequency"
                        onChange={(e) => {
                          formik.setFieldValue("frequency", e.target.value);
                          formik.setFieldValue("frequency_interval", "");
                          formik.setFieldValue("day_of_month", "");
                          formik.setFieldValue("weekday", "");
                          formik.setFieldValue("month", "");
                          formik.setFieldValue("day_of_year", "");
                          formik.setFieldValue("days_after_quarter", "");
                        }}
                        MenuProps={selectMenuProps}
                        sx={{
                          borderRadius: "8px",
                          "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                          "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                        }}
                      >
                        <MenuItem value="Yearly">Yearly</MenuItem>
                        <MenuItem value="Monthly">Monthly</MenuItem>
                        <MenuItem value="Every N Months">Every N Months</MenuItem>
                        <MenuItem value="Weekly">Weekly</MenuItem>
                        <MenuItem value="Every N Weeks">Every N Weeks</MenuItem>
                        <MenuItem value="Quarterly">Quarterly</MenuItem>
                      </Select>
                    </FormControl>
                  </Grid>
                  {(formik.values.frequency === "Every N Months" || formik.values.frequency === "Every N Weeks") && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="interval-label" sx={{ color: theme.textColor }}>Interval (N)</InputLabel>
                        <Select
                          labelId="interval-label"
                          value={formik.values.frequency_interval || ""}
                          label="Interval (N)"
                          onChange={(e) => formik.setFieldValue("frequency_interval", e.target.value)}
                          MenuProps={selectMenuProps}
                          sx={{
                            borderRadius: "8px",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                          }}
                        >
                          {Array.from(
                            { length: formik.values.frequency === "Every N Months" ? 12 : 4 },
                            (_, i) => i + 1
                          ).map((n) => (
                            <MenuItem key={n} value={String(n)}>{n}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {(formik.values.frequency === "Monthly" || formik.values.frequency === "Every N Months") && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="day-of-month-label" sx={{ color: theme.textColor }}>Day of month</InputLabel>
                        <Select
                          labelId="day-of-month-label"
                          value={formik.values.day_of_month || ""}
                          label="Day of month"
                          onChange={(e) => formik.setFieldValue("day_of_month", e.target.value)}
                          MenuProps={selectMenuProps}
                          sx={{
                            borderRadius: "8px",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                          }}
                        >
                          {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                            <MenuItem key={d} value={String(d)}>{d}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {(formik.values.frequency === "Weekly" || formik.values.frequency === "Every N Weeks") && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="weekday-label" sx={{ color: theme.textColor }}>Day of week</InputLabel>
                        <Select
                          labelId="weekday-label"
                          value={formik.values.weekday || ""}
                          label="Day of week"
                          onChange={(e) => formik.setFieldValue("weekday", e.target.value)}
                          MenuProps={selectMenuProps}
                          sx={{
                            borderRadius: "8px",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                          }}
                        >
                          <MenuItem value="Sunday">Sunday</MenuItem>
                          <MenuItem value="Monday">Monday</MenuItem>
                          <MenuItem value="Tuesday">Tuesday</MenuItem>
                          <MenuItem value="Wednesday">Wednesday</MenuItem>
                          <MenuItem value="Thursday">Thursday</MenuItem>
                          <MenuItem value="Friday">Friday</MenuItem>
                          <MenuItem value="Saturday">Saturday</MenuItem>
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                  {formik.values.frequency === "Yearly" && (
                    <>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 3
                        }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="day-of-year-label" sx={{ color: theme.textColor }}>Day</InputLabel>
                          <Select
                            labelId="day-of-year-label"
                            value={formik.values.day_of_year || ""}
                            label="Day"
                            onChange={(e) => formik.setFieldValue("day_of_year", e.target.value)}
                            MenuProps={selectMenuProps}
                            sx={{
                              borderRadius: "8px",
                              "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                            }}
                          >
                            {Array.from({ length: 28 }, (_, i) => i + 1).map((d) => (
                              <MenuItem key={d} value={String(d)}>{d}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                      <Grid
                        size={{
                          xs: 12,
                          sm: 3
                        }}>
                        <FormControl fullWidth size="small">
                          <InputLabel id="month-label" sx={{ color: theme.textColor }}>Month</InputLabel>
                          <Select
                            labelId="month-label"
                            value={formik.values.month || ""}
                            label="Month"
                            onChange={(e) => formik.setFieldValue("month", e.target.value)}
                            MenuProps={selectMenuProps}
                            sx={{
                              borderRadius: "8px",
                              "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                              "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                            }}
                          >
                            {Array.from({ length: 12 }, (_, i) => i + 1).map((m) => (
                              <MenuItem key={m} value={String(m)}>{m}</MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Grid>
                    </>
                  )}
                  {formik.values.frequency === "Quarterly" && (
                    <Grid
                      size={{
                        xs: 12,
                        sm: 6
                      }}>
                      <FormControl fullWidth size="small">
                        <InputLabel id="days-after-quarter-label" sx={{ color: theme.textColor }}>Days after quarter start</InputLabel>
                        <Select
                          labelId="days-after-quarter-label"
                          value={formik.values.days_after_quarter ?? ""}
                          label="Days after quarter start"
                          onChange={(e) => formik.setFieldValue("days_after_quarter", e.target.value)}
                          MenuProps={selectMenuProps}
                          sx={{
                            borderRadius: "8px",
                            "& .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                            "&.Mui-focused .MuiOutlinedInput-notchedOutline": { borderColor: theme.primary },
                          }}
                        >
                          {Array.from({ length: 91 }, (_, i) => i).map((d) => (
                            <MenuItem key={d} value={String(d)}>{d}</MenuItem>
                          ))}
                        </Select>
                      </FormControl>
                    </Grid>
                  )}
                </Grid>
              )}
            </StyledCard>

          </StyledCard>

          {/* Personal Information */}
          <StyledCard elevation={0}>
            <SectionTitle textColor={theme.textColor}>
              Your Information
            </SectionTitle>
            <Grid container spacing={2.5}>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <InputText
                  fullWidth
                  label="First Name"
                  name="firstName"
                  required
                  disable={!!donorProfile}
                  value={formik.values.firstName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.firstName && Boolean(formik.errors.firstName)}
                  helperText={formik.touched.firstName && formik.errors.firstName}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <InputText
                  fullWidth
                  label="Last Name"
                  name="lastName"
                  required
                  disable={!!donorProfile}
                  value={formik.values.lastName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.lastName && Boolean(formik.errors.lastName)}
                  helperText={formik.touched.lastName && formik.errors.lastName}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <InputText
                  fullWidth
                  label="Email Address"
                  name="email"
                  type="email"
                  required
                  disable={!!donorProfile}
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  error={formik.touched.email && Boolean(formik.errors.email)}
                  helperText={formik.touched.email && formik.errors.email}
                />
              </Grid>
              <Grid
                size={{
                  xs: 12,
                  sm: 6
                }}>
                <InputText
                  fullWidth
                  label="Phone Number"
                  name="phone"
                  required
                  disable={!!donorProfile}
                  value={formik.values.phone}
                  onChange={(e) => formik.setFieldValue("phone", formatPhoneNumber(e.target.value))}
                  onBlur={formik.handleBlur}
                  error={formik.touched.phone && Boolean(formik.errors.phone)}
                  helperText={formik.touched.phone && formik.errors.phone}
                  placeholder="(555) 555-5555"
                />
              </Grid>
              <Grid size={12}>
                <Box sx={{ pointerEvents: donorProfile ? "none" : "auto", opacity: donorProfile ? 0.7 : 1 }}>
                  <Address
                    handleChange={handleAddressChange}
                    formik={formik}
                    submitAttempted={formSubmitAttempted}
                  />
                </Box>
              </Grid>
            </Grid>
          </StyledCard>

          {/* Payment Information */}
          <StyledCard elevation={0}>
            <SectionTitle textColor={theme.textColor}>
              <LockIcon sx={{ color: theme.iconColor, fontSize: 20 }} />
              Secure Payment
            </SectionTitle>

            {companyData?.creditCardEnabled === false ? (
              <Alert severity="info" sx={{ "& .MuiAlert-message": { fontSize: "14px" } }}>
                For this company, credit/debit card is disabled. Card payments are not accepted for this organization.
              </Alert>
            ) : (
            isDejavoo ? (
              <>
                {(dejavoo?.status === "loading" || dejavoo?.status === "uninitialized") && (
                  <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                    <CircularProgress size={40} sx={{ color: theme.primary }} />
                    <Typography sx={{ ml: 2, color: "#666" }}>
                      Loading payment fields...
                    </Typography>
                  </Box>
                )}
                {dejavoo?.status === "error" && (
                  <Alert severity="error">{dejavoo?.error || "Failed to load payment."}</Alert>
                )}
                {dejavoo?.status === "loaded" && (
                  <Box className="dejavoo-native-card-fields">
                  <Grid container spacing={2}>
                    {formSubmitAttempted && (dejavooCardErrors.ccnumber || dejavooCardErrors.ccexp || dejavooCardErrors.cvv) && (
                      <Grid size={12}>
                        <Typography sx={{ color: "#d32f2f", fontSize: "12px", mb: 1 }}>
                          Please complete all card fields correctly.
                        </Typography>
                      </Grid>
                    )}
                    <Grid size={12}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          "& .cs-input-shell": {
                            borderColor: dejavooCardErrors.ccnumber
                              ? "#B40E3E !important"
                              : dejavooCardValid.ccnumber && dejavooCardData.ccnumber
                                ? "#14855F !important"
                                : undefined,
                          },
                          "& .cs-input": {
                            color: dejavooCardErrors.ccnumber
                              ? "#B40E3E !important"
                              : dejavooCardValid.ccnumber && dejavooCardData.ccnumber
                                ? "#14855F !important"
                                : undefined,
                          },
                        }}
                      >
                        <label className="cs-rs__label" htmlFor="ccnumber">
                          Credit Card
                          <span className="cs-field-label-required" aria-hidden="true">
                            {" "}
                            *
                          </span>
                        </label>
                        <InputText
                          fullWidth
                          name="ccnumber"
                          placeholder="13–19 digits"
                          value={formatCardNumberDisplay(dejavooCardData.ccnumber)}
                          onChange={handleDejavooCardChange}
                          error={!!dejavooCardErrors.ccnumber}
                          inputProps={{ "data-name": "ccnumber" }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          "& .cs-input-shell": {
                            borderColor: dejavooCardErrors.ccexp
                              ? "#B40E3E !important"
                              : dejavooCardValid.ccexp && dejavooCardData.ccexpiry
                                ? "#14855F !important"
                                : undefined,
                          },
                          "& .cs-input": {
                            color: dejavooCardErrors.ccexp
                              ? "#B40E3E !important"
                              : dejavooCardValid.ccexp && dejavooCardData.ccexpiry
                                ? "#14855F !important"
                                : undefined,
                          },
                        }}
                      >
                        <label className="cs-rs__label" htmlFor="ccexpiry">
                          MM / YY
                          <span className="cs-field-label-required" aria-hidden="true">
                            {" "}
                            *
                          </span>
                        </label>
                        <InputText
                          fullWidth
                          name="ccexpiry"
                          placeholder="MM/YY"
                          value={formatExpiryDisplay(dejavooCardData.ccexpiry)}
                          onChange={handleDejavooCardChange}
                          error={!!dejavooCardErrors.ccexp}
                          inputProps={{ "data-name": "ccexpiry", maxLength: 5 }}
                        />
                      </Box>
                    </Grid>
                    <Grid size={6}>
                      <Box
                        sx={{
                          display: "flex",
                          flexDirection: "column",
                          gap: "10px",
                          "& .cs-input-shell": {
                            borderColor: dejavooCardErrors.cvv
                              ? "#B40E3E !important"
                              : dejavooCardValid.cvv && dejavooCardData.cccvv
                                ? "#14855F !important"
                                : undefined,
                          },
                          "& .cs-input": {
                            color: dejavooCardErrors.cvv
                              ? "#B40E3E !important"
                              : dejavooCardValid.cvv && dejavooCardData.cccvv
                                ? "#14855F !important"
                                : undefined,
                          },
                        }}
                      >
                        <label className="cs-rs__label" htmlFor="cccvv">
                          CVV
                          <span className="cs-field-label-required" aria-hidden="true">
                            {" "}
                            *
                          </span>
                        </label>
                        <InputText
                          fullWidth
                          name="cccvv"
                          placeholder="3–4 digits"
                          value={dejavooCardData.cccvv}
                          onChange={handleDejavooCardChange}
                          error={!!dejavooCardErrors.cvv}
                          inputProps={{ "data-name": "cccvv", maxLength: 4 }}
                        />
                      </Box>
                    </Grid>
                  </Grid>
                  </Box>
                )}
              </>
            ) : scriptGenerating ? (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", py: 4 }}>
                <CircularProgress size={40} sx={{ color: theme.primary }} />
                <Typography sx={{ ml: 2, color: "#666" }}>
                  Loading payment fields...
                </Typography>
              </Box>
            ) : scriptError ? (
              <Alert severity="error">{scriptError}</Alert>
            ) : !companyData?.PublicKey ? (
              <Alert severity="warning">
                Payment gateway is not configured for this organization. Please contact the organization for assistance.
              </Alert>
            ) : (
              <div id="payment-fields">
                {formSubmitAttempted && cardFieldErrors.cardError && (
                  <Typography
                    sx={{
                      color: "#d32f2f",
                      fontSize: "12px",
                      mb: 2,
                    }}
                  >
                    {cardFieldErrors.cardError}
                  </Typography>
                )}
                <Grid container spacing={2}>
                  <Grid size={12}>
                    <div
                      id="ccnumber"
                      className="payment-field"
                      style={{
                        height: "50px",
                        border: formSubmitAttempted && cardFieldErrors.ccnumber 
                          ? "1px solid #d32f2f" 
                          : `1px solid ${theme.primary}`,
                        borderRadius: "8px",
                        backgroundColor: "white",
                      }}
                    />
                    {formSubmitAttempted && cardFieldErrors.ccnumber && (
                      <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5, ml: 1.5 }}>
                        {cardFieldErrors.ccnumber}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={6}>
                    <div
                      id="ccexp"
                      className="payment-field"
                      style={{
                        height: "50px",
                        border: formSubmitAttempted && cardFieldErrors.ccexp 
                          ? "1px solid #d32f2f" 
                          : `1px solid ${theme.primary}`,
                        borderRadius: "8px",
                        backgroundColor: "white",
                      }}
                    />
                    {formSubmitAttempted && cardFieldErrors.ccexp && (
                      <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5, ml: 1.5 }}>
                        {cardFieldErrors.ccexp}
                      </Typography>
                    )}
                  </Grid>
                  <Grid size={6}>
                    <div
                      id="cvv"
                      className="payment-field"
                      style={{
                        height: "50px",
                        border: formSubmitAttempted && cardFieldErrors.cvv 
                          ? "1px solid #d32f2f" 
                          : `1px solid ${theme.primary}`,
                        borderRadius: "8px",
                        backgroundColor: "white",
                      }}
                    />
                    {formSubmitAttempted && cardFieldErrors.cvv && (
                      <Typography sx={{ color: "#d32f2f", fontSize: "12px", mt: 0.5, ml: 1.5 }}>
                        {cardFieldErrors.cvv}
                      </Typography>
                    )}
                  </Grid>
                </Grid>
              </div>
            )
            )}
          </StyledCard>

          {/* Transaction Fee Breakdown */}
          {formik.values.amount && cardFeeConfig?.FeeAmount > 0 && (
            <StyledCard elevation={0} sx={{ mb: 3 }}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ color: "#666", fontSize: "14px" }}>
                    Donation Amount:
                  </Typography>
                  <Typography sx={{ color: theme.textColor, fontWeight: 600, fontSize: "14px" }}>
                    ${parseFloat(formik.values.amount).toFixed(2)}
                  </Typography>
                </Box>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ color: "#666", fontSize: "14px" }}>
                    {cardFeeConfig?.FeeLabel || "Convenience Fee"}
                    {cardFeeConfig?.FeeType === "Percentage" && ` (${cardFeeConfig.FeeAmount}%)`}:
                  </Typography>
                  <Typography sx={{ color: theme.textColor, fontWeight: 600, fontSize: "14px" }}>
                    ${calculateTotals(formik.values.amount).convenienceFee.toFixed(2)}
                  </Typography>
                </Box>
                <Divider sx={{ my: 0.5 }} />
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography sx={{ color: theme.textColor, fontWeight: 700, fontSize: "16px" }}>
                    Total:
                  </Typography>
                  <Typography sx={{ color: theme.primary, fontWeight: 700, fontSize: "18px" }}>
                    ${calculateTotals(formik.values.amount).total.toFixed(2)}
                  </Typography>
                </Box>
              </Box>
            </StyledCard>
          )}

          {/* Submit Button */}
          <Button
            fullWidth
            type="submit"
            disabled={
              submitting ||
              companyData?.creditCardEnabled === false ||
              (isDejavoo
                ? !dejavoo?.postData || !dejavooFieldsValid
                : scriptGenerating)
            }
            sx={{
              borderRadius: "12px",
              padding: "18px",
              background: `linear-gradient(135deg, ${theme.primary} 0%, ${theme.primary}dd 100%)`,
              color: "white",
              fontSize: "18px",
              fontWeight: 600,
              textTransform: "none",
              boxShadow: `0 6px 20px ${theme.primary}59`,
              transition: "all 0.3s ease",
              "&:hover": {
                background: `linear-gradient(135deg, ${theme.primary}dd 0%, ${theme.primary}bb 100%)`,
                boxShadow: `0 8px 25px ${theme.primary}73`,
                transform: "translateY(-2px)",
              },
              "&:disabled": {
                background: "#94a3b8",
                boxShadow: "none",
                transform: "none",
              },
            }}
          >
            {submitting ? (
              <CircularProgress size={26} sx={{ color: "white" }} />
            ) : (
              <>
                <FavoriteIcon sx={{ mr: 1.5, fontSize: 24, color: theme.iconColor }} />
                Complete Donation - ${formik.values.amount ? calculateTotals(formik.values.amount).total.toFixed(2) : "0.00"}
              </>
            )}
          </Button>

          {/* Security Note */}
          <Box 
            sx={{ 
              textAlign: "center", 
              mt: 3,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.5,
            }}
          >
            <LockIcon sx={{ fontSize: 14, color: "#94a3b8" }} />
            <Typography variant="caption" sx={{ color: "#64748b" }}>
              Your payment is secured with 256-bit SSL encryption
            </Typography>
          </Box>
        </form>
      </Grid>
      {/* Footer */}
      <Box
        sx={{
          textAlign: "center",
          py: 3,
          mt: 4,
          borderTop: "1px solid #e2e8f0",
        }}
      >
        {companyData?.phoneNumber && (
          <Typography variant="body2" sx={{ color: "#666", mb: 1 }}>
            {companyData.companyName} | {companyData.phoneNumber}
          </Typography>
        )}
        {companyData?.email && (
          <Typography variant="body2" sx={{ color: "#666", mb: 2 }}>
            {companyData.email}
          </Typography>
        )}
        <Typography variant="caption" sx={{ color: "#888" }}>
          Powered by Cloud Donor Manager
        </Typography>
      </Box>
    </Grid>
  );
};

export default PublicDonation;
